import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { UserAuth } from "../../utils/AuthContext";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import PageWrapper from "../../components/PageWrapper";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import searchIcon from "../../assets/search_icon.png";

const SellInvoices = () => {
  // ====================
  // 1. Context & Translations
  // ====================
  const { userData } = useContext(UserAuth);
  const { t } = useTranslation("general");

  // ====================
  // 2. State Management
  // ====================
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "id",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // ====================
  // 3. Constants
  // ====================
  const titles = [
    { value: "invoiceNumber" },
    { value: "customerName" }, // Added back customer name
    { value: "Date" },
    { value: "amount" },
    { value: "received" }, // Added back received amount
  ];

  const searchTitles = [
    { title: t("invoiceNumber"), value: "id", type: "text" },
    { title: t("customerName"), value: "customer", type: "text" },
    { title: t("finalAmount"), value: "finalAmount", type: "money" },
  ];

  // ====================
  // 4. API Functions
  // ====================
  const getAllSellInvoice = async (page = 1, limit = 5, searchTerm = "") => {
    try {
      const params = {
        page,
        limit,
        organizationId: userData?.organizationId,
        categories: userData?.organizationType,
      };

      // Always include date parameters if they exist
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Add search parameters based on selected type
      if (selectedTitle.value !== "date") {
        params.searchQuery = searchTerm;
        params.searchType = selectedTitle.value;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllSellInvoices`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
          params,
        }
      );

      if (response.status === 200) {
        setInvoices(response.data.data);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.totalCount,
        });
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };
  // const getAllSellInvoice = async (page = 1, limit = 5, searchTerm = "") => {
  //   try {
  //     const params = {
  //       page,
  //       limit,
  //       organizationId: userData?.organizationId,
  //       categories: userData?.organizationType,
  //     };

  //     if (selectedTitle.value === "date") {
  //       params.startDate = startDate;
  //       params.endDate = endDate;
  //       params.searchType = "date";
  //     } else {
  //       params.searchQuery = searchTerm;
  //       params.searchType = selectedTitle.value;
  //     }

  //     const response = await axios.get(
  //       `${process.env.REACT_APP_URL}/getAllSellInvoices`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${userData?.token}`,
  //         },
  //         params,
  //       }
  //     );

  //     if (response.status === 200) {
  //       setInvoices(response.data.data);
  //       setPagination({
  //         currentPage: response.data.pagination.currentPage,
  //         totalPages: response.data.pagination.totalPages,
  //         totalRecords: response.data.pagination.totalCount,
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error fetching invoices:", error);
  //   }
  // };

  // ====================
  // 5. Handler Functions
  // ====================
  const handleSearchType = (type) => {
    setSelectedTitle(type);
    // if (type.value === "date") {
    //   setSearchQuery("");
    // } else {
    //   setStartDate("");
    //   setEndDate("");
    // }
  };

  // const handleSearch = () => {
  //   if (selectedTitle.value === "date" && (!startDate || !endDate)) {
  //     alert("Please select both start and end dates");
  //     return;
  //   }
  //   getAllSellInvoice(1, 5, searchQuery);
  // };

  const handleSearch = () => {
    // Validate date range if both dates are provided
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start date must be before end date");
      return;
    }
    getAllSellInvoice(1, 5, searchQuery);
  };
  
  // ====================
  // 6. Effects
  // ====================
  useEffect(() => {
    getAllSellInvoice(pagination.currentPage, 5, searchQuery);
  }, [pagination.currentPage]);

  // ====================
  // 7. Table Data Preparation
  // ====================
  // ... (keep all the imports and initial code the same as before)

  // ====================
  // 7. Table Data Preparation
  // ====================
  const invoicesTable = {
    titles,
    rows: invoices.map((invoice) => [
      { value: invoice.id, type: TABLE_CELL_TYPES.TEXT },
      { value: invoice.customer?.name || "-", type: TABLE_CELL_TYPES.TEXT }, // Customer name
      {
        value: new Date(invoice.date).toLocaleDateString(),
        type: TABLE_CELL_TYPES.TEXT,
      },
      {
        value: `${invoice.finalAmount}`,
        type: TABLE_CELL_TYPES.MONEY,
      },
      {
        value: `${invoice.receivedAmount}`, // Received amount
        type: TABLE_CELL_TYPES.MONEY,
      },
    ]),
  };

  // ... (keep the rest of the code the same)

  // ====================
  // 8. Render
  // ====================
  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_300 u-padding--bottom_500">
        <h2>{t("invoicesList")}</h2>

        <div className="o-column o-column--gap-size_100 u-padding--bottom_500">
          {/* Search section */}
          <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400">
            <MultipleSearchFilter
              options={searchTitles}
              selected={selectedTitle}
              setSelected={handleSearchType}
              handleSearch={handleSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              flex
            />

            {/* <CustomButton
              title={t("search")}
              onClick={handleSearch}
              icon={searchIcon}
              iconSize={15}
              classList="c-search-button"
            /> */}
          </section>
        </div>

        {/* Invoices Table */}
        <RoundedTable
          titles={invoicesTable.titles}
          rows={invoicesTable.rows}
          pagination={pagination}
          setPagination={setPagination}
        />
      </div>
    </PageWrapper>
  );
};

export default SellInvoices;
